/**
 * Il tema della cornice dell'app: cosa si scrive nel DOM, e la chiave specchio.
 *
 * La FONTE DI VERITA' e' `usePreferencesStore.uiTheme`, che si sincronizza
 * sull'account. Qui ci sono solo i due mestieri che il DOM richiede e che uno
 * store non puo' fare da se': scrivere l'attributo su `<html>`, e lasciare una
 * briciola che lo script pre-disegno di `index.html` possa leggere.
 *
 * ── Perche' una chiave piatta e non il blob delle preferenze ────────────────
 * Lo script che gira PRIMA del primo disegno deve essere sincrono e minuscolo:
 * non puo' conoscere l'involucro `{state, version}` di zustand ne' fare il
 * `JSON.parse` di venticinque chiavi prima del primo pixel. `draughts-theme` e'
 * quindi una cache con UN SOLO LETTORE, scritta da `applicaTema` a ogni
 * cambio e a ogni idratazione dal server. Il blob resta la verita'.
 *
 * ── Perche' soltanto l'attributo, e nessun token ────────────────────────────
 * Uno `style` IN LINEA su `<html>` scavalca ogni foglio di stile, attributo
 * compreso: lo fanno gia' `BoardThemeApplier` (i colori delle caselle),
 * `useAltezzaBarra`. Un applicatore che scrivesse valori di
 * token sarebbe percio' non scavalcabile da nessun CSS — nemmeno da una regola
 * piu' specifica — e litigherebbe con quello della scacchiera. Qui si scrive
 * `data-theme` e basta; i valori stanno in `theme-dark.css`, dove si possono
 * leggere, misurare e scavalcare.
 */
import type { UiTheme } from '@draughts/shared';

/** La briciola per lo script pre-disegno. Un solo lettore: `index.html`. */
export const THEME_MIRROR_KEY = 'draughts-theme';

/**
 * Il colore della barra di sistema, per tema.
 *
 * Deve combaciare con `--color-bg` dei due temi: il valore chiaro con quello di
 * `index.css`, lo scuro con quello di `theme-dark.css`. Il terzo posto in cui lo
 * stesso scuro e' scritto a mano e' lo script in `index.html`, che non puo'
 * importare niente — se cambia qui, cambia anche la'.
 */
export const THEME_COLOR: Record<UiTheme, string> = {
  light: '#F5F0E6',
  dark: '#191714',
};

function leggiSpecchio(): string | null {
  try {
    return localStorage.getItem(THEME_MIRROR_KEY);
  } catch {
    // Storage inaccessibile (navigazione privata, cookie bloccati): si cade sul
    // chiaro, cioe' sul tema di default — nessun guasto visibile.
    return null;
  }
}

/** Il tema registrato su QUESTO dispositivo, se ce n'e' uno valido. */
export function temaDalloSpecchio(): UiTheme | null {
  const grezzo = leggiSpecchio();
  return grezzo === 'dark' || grezzo === 'light' ? grezzo : null;
}

function scriviSpecchio(tema: UiTheme): void {
  try {
    localStorage.setItem(THEME_MIRROR_KEY, tema);
  } catch {
    // Vedi sopra: il prezzo e' un lampo chiaro al prossimo avvio, non un errore.
  }
}

/**
 * Mette il tema nel DOM: attributo, specchio, colore della barra di sistema.
 *
 * L'attributo si scrive SEMPRE, anche per il chiaro: cosi' il tema in vigore e'
 * un fatto leggibile negli strumenti del browser e nello script del giro di
 * verifica, invece di essere un'assenza. Il chiaro resta comunque «nessuna
 * regola che scatta», quindi e' esattamente il CSS di prima che questo tema
 * esistesse.
 */
export function applicaTema(tema: UiTheme): void {
  document.documentElement.setAttribute('data-theme', tema);
  scriviSpecchio(tema);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[tema]);
}
